import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Today, CalendarMonth, Close, DateRange } from '@mui/icons-material';

const MuiDateRangeSelector = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  handleApplyDateRange,
  open: propOpen,
  onClose: propClose,
  showTrigger = true
}) => {
 
  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = propOpen !== undefined ? propOpen : localOpen;

  const handleClose = () => {
    if (propClose) {
      propClose();
    } else {
      setLocalOpen(false);
    }
  };
 
  const handleApply = () => {
    handleClose();
    handleApplyDateRange();
  };

  const handleClear = () => {
    setStartDate(dayjs().subtract(6, 'day'));
    setEndDate(dayjs());
  };

  const formatDateRange = () => {
    if (startDate.isSame(endDate, 'day')) {
      return startDate.format('DD/MM/YYYY');
    }
    return `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;
  };

  return (
    <Box>
      {/* Trigger Button */}
      {showTrigger && (
        <Button
          variant="outlined"
          startIcon={<DateRange />}
          onClick={() => setLocalOpen(true)}
          sx={{
            justifyContent: 'space-between',
            px: 2,
            py: 1.3,
          }}
        >
          <Typography variant="body2">{formatDateRange()}</Typography>
        </Button>
      )}

      {/* Date Range Picker Dialog */}
      <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Select Date Range</Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3}>
            {/* Date Pickers */}
            <Grid item xs={12} md={12}>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography fontWeight={700}>From Date</Typography>
                      <DatePicker
                        value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                        }}
                        format="DD/MM/YYYY"
                        maxDate={endDate}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography fontWeight={700}>To Date</Typography>
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={endDate}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                        }}
                        minDate={startDate}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>

                {/* Selected Range Preview */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 2,
                    bgcolor: 'grey.50',
                    borderColor: 'grey.300',
                  }}
                >
                  <Typography variant="body2">
                    Selected Range:
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatDateRange()}
                  </Typography>
                  <Typography variant="caption">
                    {Math.abs(endDate.diff(startDate, 'day') + 1)} days
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClear} color="inherit">
            Clear
          </Button>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleApply} variant="contained" startIcon={<Today />}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MuiDateRangeSelector;
